import { useEffect, useState } from 'react';
import { AlertTriangle, CheckCircle2, Loader2, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { accountingError } from '@/lib/accounting';

const PREVIEW = 8;

function plural(count, noun) {
  return `${count} ${noun}${count === 1 ? '' : 's'}`;
}

/**
 * Delete several records, and say honestly what happened to each.
 *
 * Deletion in Accounting is guarded per record — an invoice a payment names,
 * an item already on a document, a bill with money against it — so a bulk
 * delete is *expected* to partially fail, and a bar that reports "3 failed"
 * without saying which three, or why, would be worse than no bulk action.
 *
 * So this runs them **one at a time, carrying on past a refusal**, and ends on
 * a result screen naming every record that was kept and the server's own
 * reason for keeping it.
 *
 * `cascadeOf` is what *else* each deletion removes — a payment off a student's
 * ledger, a payout off an agency's record. Those sentences come from the server
 * with the rows, are de-duplicated, and are shown before anybody confirms,
 * because a bulk delete that quietly reached into six student profiles would be
 * the worst possible thing to discover afterwards.
 *
 * It calls the same single-record endpoint the row button calls, once per row,
 * rather than a bulk route. That is deliberate: the guards and their wording
 * then exist in exactly one place and cannot drift between "delete this" and
 * "delete these". The cost is one request per record, which at a page of at
 * most a hundred rows is a fair trade for not maintaining two sets of rules.
 */
export default function BulkDeleteDialog({
  open,
  onOpenChange,
  noun = 'record',
  rows = [],
  labelOf = (row) => row?.name || row?.id,
  cascadeOf,
  remove,
  onFinished,
}) {
  const [progress, setProgress] = useState(null);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!open) {
      setProgress(null);
      setResult(null);
    }
  }, [open]);

  const run = async () => {
    const deleted = [];
    const failed = [];
    for (let index = 0; index < rows.length; index += 1) {
      const row = rows[index];
      setProgress({ done: index, total: rows.length });
      try {
        // eslint-disable-next-line no-await-in-loop
        await remove(row);
        deleted.push(labelOf(row));
      } catch (err) {
        failed.push({
          label: labelOf(row),
          reason: accountingError(err, 'It could not be deleted.'),
        });
      }
    }
    setProgress(null);
    setResult({ deleted, failed });
    onFinished?.({ deleted, failed });
  };

  const running = Boolean(progress);

  // Several rows can share one consequence — six tuition payments all say the
  // same thing about the student ledger — so the sentences are de-duplicated
  // rather than repeated once per row.
  const cascades = cascadeOf
    ? [...new Set(rows.map((row) => cascadeOf(row)).filter(Boolean))]
    : [];

  return (
    <Dialog open={open} onOpenChange={(next) => !running && onOpenChange(next)}>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg">
        {result ? (
          <>
            <DialogHeader>
              <DialogTitle>
                {result.failed.length === 0
                  ? `${plural(result.deleted.length, noun)} deleted`
                  : `${plural(result.deleted.length, noun)} deleted, ${result.failed.length} kept`}
              </DialogTitle>
              <DialogDescription>
                {result.failed.length === 0
                  ? 'Everything selected has been removed.'
                  : 'The rest were kept, each for the reason below.'}
              </DialogDescription>
            </DialogHeader>
            {result.failed.length > 0 ? (
              <ul className="space-y-2">
                {result.failed.map((item, index) => (
                  <li
                    key={index}
                    className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900"
                  >
                    <p className="font-semibold">{item.label}</p>
                    <p className="mt-0.5 leading-relaxed">{item.reason}</p>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800">
                <CheckCircle2 className="size-4 shrink-0" />
                Done.
              </div>
            )}
            <DialogFooter>
              <Button type="button" onClick={() => onOpenChange(false)}>
                Close
              </Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>Delete {plural(rows.length, noun)}?</DialogTitle>
              <DialogDescription>
                This cannot be undone. Anything the portal still depends on will be kept, and
                named afterwards.
              </DialogDescription>
            </DialogHeader>
            <ul className="space-y-1 text-xs text-[#062B1A]/70">
              {rows.slice(0, PREVIEW).map((row, index) => (
                <li key={index} className="truncate">
                  {labelOf(row)}
                </li>
              ))}
              {rows.length > PREVIEW && (
                <li className="text-[#062B1A]/45">
                  and {rows.length - PREVIEW} more
                </li>
              )}
            </ul>
            <div className="space-y-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs leading-relaxed text-amber-800">
              <div className="flex items-start gap-2">
                <AlertTriangle className="mt-0.5 size-3.5 shrink-0" />
                <p>Each is deleted separately, so a refusal stops that one and nothing else.</p>
              </div>
              {cascades.map((sentence, index) => (
                <p key={index} className="pl-[1.375rem]">
                  {sentence}
                </p>
              ))}
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                disabled={running}
                onClick={() => onOpenChange(false)}
              >
                Cancel
              </Button>
              <Button
                type="button"
                onClick={run}
                disabled={running || rows.length === 0}
                className="bg-red-700 text-white hover:bg-red-800"
              >
                {running ? (
                  <>
                    <Loader2 className="mr-2 size-4 animate-spin" />
                    Deleting {progress.done + 1} of {progress.total}…
                  </>
                ) : (
                  <>
                    <Trash2 className="mr-2 size-4" />
                    Delete {plural(rows.length, noun)}
                  </>
                )}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

/**
 * The bar that appears above a table once something is ticked.
 *
 * Absent entirely at zero, rather than present and disabled: a permanently
 * greyed "Delete selected" is furniture, and this table already has enough.
 */
export function BulkDeleteBar({ count, noun = 'record', onDelete, onClear, disabled }) {
  if (!count) return null;
  return (
    <div className="flex flex-wrap items-center gap-3 rounded-lg border border-[#062B1A]/12 bg-[#F7F8FA] px-3 py-2">
      <span className="text-sm font-medium text-[#062B1A]">{plural(count, noun)} selected</span>
      <Button
        type="button"
        size="sm"
        disabled={disabled}
        onClick={onDelete}
        className="h-8 bg-red-700 text-white hover:bg-red-800"
      >
        <Trash2 className="mr-1.5 size-3.5" />
        Delete selected
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="h-8 px-2 text-xs text-[#062B1A]/60"
        onClick={onClear}
      >
        Clear
      </Button>
    </div>
  );
}
